/**
 * @fileoverview Panel de estado OAuth2 para Nubelytics
 * 
 * Componente React que muestra el estado del sistema OAuth2 en tiempo real,
 * incluyendo métricas de autenticación, errores y diagnósticos.
 * Implementa principios de diseño limpio y UX moderna.
 * 
 * @author Nubelytics Engineering Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  Button
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import oauth2Monitor from '../../infrastructure/monitoring/OAuth2Monitor';

/**
 * Componente principal del panel de estado OAuth2
 */
const OAuth2StatusPanel = ({ compact = false }) => {
  const theme = useTheme();
  const [metrics, setMetrics] = useState({});
  const [diagnostics, setDiagnostics] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  /**
   * Actualiza las métricas del monitor
   */
  const updateMetrics = useCallback(() => {
    setMetrics(oauth2Monitor.getMetrics());
    setDiagnostics(oauth2Monitor.getDiagnostics());
    setLastUpdate(new Date());
  }, []);

  /**
   * Configura listeners del monitor
   */
  useEffect(() => {
    // Actualización inicial
    updateMetrics();

    // Listeners para eventos del monitor
    const handleTokenRequest = () => updateMetrics();
    const handleNetworkRequest = () => updateMetrics();
    const handleAuthError = () => updateMetrics();
    const handleHealthChange = () => updateMetrics();

    oauth2Monitor.on('tokenRequest', handleTokenRequest);
    oauth2Monitor.on('networkRequest', handleNetworkRequest);
    oauth2Monitor.on('authError', handleAuthError);
    oauth2Monitor.on('healthChange', handleHealthChange);

    // Actualización periódica
    const interval = setInterval(updateMetrics, 5000);

    return () => {
      oauth2Monitor.off('tokenRequest', handleTokenRequest);
      oauth2Monitor.off('networkRequest', handleNetworkRequest);
      oauth2Monitor.off('authError', handleAuthError);
      oauth2Monitor.off('healthChange', handleHealthChange);
      clearInterval(interval);
    };
  }, [updateMetrics]);

  /**
   * Obtiene el color del estado de salud
   */
  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'critical':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  /**
   * Obtiene el icono del estado de salud
   */
  const getHealthIcon = (health) => {
    switch (health) {
      case 'healthy':
        return <CheckCircleIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'critical':
        return <ErrorIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  /**
   * Exporta diagnósticos
   */
  const handleExportDiagnostics = () => {
    const diagnosticsData = oauth2Monitor.exportDiagnostics();
    const blob = new Blob([diagnosticsData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oauth2-diagnostics-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Formatea la fecha para mostrar
   */
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  if (compact) {
    return (
      <Chip
        icon={getHealthIcon(metrics.systemHealth)}
        label={`OAuth2: ${metrics.systemHealth || 'unknown'}`}
        color={metrics.systemHealth === 'healthy' ? 'success' : 
               metrics.systemHealth === 'warning' ? 'warning' : 'error'}
        variant="outlined"
        size="small"
      />
    );
  }

  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Estado OAuth2</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Actualizar">
              <IconButton onClick={updateMetrics} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Exportar diagnósticos">
              <IconButton onClick={handleExportDiagnostics} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setExpanded(!expanded)} size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Estado de salud principal */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {getHealthIcon(metrics.systemHealth)}
            <Typography variant="body1" fontWeight="bold">
              {metrics.systemHealth || 'unknown'}
            </Typography>
          </Box>
          <Chip
            label={`${metrics.consecutiveErrors || 0} errores consecutivos`}
            color={metrics.consecutiveErrors > 0 ? 'error' : 'success'}
            size="small"
          />
        </Box>

        {/* Métricas básicas */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary">
                {metrics.tokenRequests || 0}
              </Typography>
              <Typography variant="caption">Solicitudes</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="success.main">
                {metrics.tokenRefreshes || 0}
              </Typography>
              <Typography variant="caption">Refreshes</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="error.main">
                {metrics.authErrors || 0}
              </Typography>
              <Typography variant="caption">Errores</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="warning.main">
                {((metrics.errorRate || 0) * 100).toFixed(1)}%
              </Typography>
              <Typography variant="caption">Tasa Error</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Barra de progreso de salud */}
        <Box mb={2}>
          <Typography variant="body2" gutterBottom>
            Salud del Sistema
          </Typography>
          <LinearProgress
            variant="determinate"
            value={metrics.systemHealth === 'healthy' ? 100 : 
                   metrics.systemHealth === 'warning' ? 60 : 20}
            color={metrics.systemHealth === 'healthy' ? 'success' : 
                   metrics.systemHealth === 'warning' ? 'warning' : 'error'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Información expandida */}
        <Collapse in={expanded}>
          <Divider sx={{ mb: 2 }} />
          
          {/* Alertas de salud */}
          {metrics.systemHealth !== 'healthy' && (
            <Alert 
              severity={metrics.systemHealth === 'warning' ? 'warning' : 'error'}
              sx={{ mb: 2 }}
            >
              {metrics.systemHealth === 'warning' 
                ? 'Sistema OAuth2 en estado de advertencia. Monitorear errores.'
                : 'Sistema OAuth2 en estado crítico. Revisar configuración.'}
            </Alert>
          )}

          {/* Detalles de métricas */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Última Actividad
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Último Refresh"
                    secondary={formatDate(metrics.lastTokenRefresh)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Último Error"
                    secondary={formatDate(metrics.lastAuthError)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Actualización"
                    secondary={formatDate(lastUpdate)}
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Diagnósticos Recientes
              </Typography>
              <List dense>
                {diagnostics.errorHistory?.slice(-3).map((error, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={error.type}
                      secondary={`${formatDate(error.timestamp)} - ${error.error?.message || 'Error desconocido'}`}
                    />
                  </ListItem>
                ))}
                {(!diagnostics.errorHistory || diagnostics.errorHistory.length === 0) && (
                  <ListItem>
                    <ListItemText
                      primary="Sin errores recientes"
                      secondary="Sistema funcionando correctamente"
                    />
                  </ListItem>
                )}
              </List>
            </Grid>
          </Grid>

          {/* Estadísticas de red */}
          <Typography variant="subtitle2" gutterBottom>
            Estadísticas de Red (últimas 24h)
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Chip
              icon={<TimelineIcon />}
              label={`${diagnostics.networkRequests?.length || 0} requests`}
              variant="outlined"
              size="small"
            />
            <Chip
              icon={<CheckCircleIcon />}
              label={`${diagnostics.networkRequests?.filter(r => r.success).length || 0} exitosos`}
              color="success"
              variant="outlined"
              size="small"
            />
            <Chip
              icon={<ErrorIcon />}
              label={`${diagnostics.networkRequests?.filter(r => !r.success).length || 0} fallidos`}
              color="error"
              variant="outlined"
              size="small"
            />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default OAuth2StatusPanel;
